import { useEffect, useState } from "react";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { kirkos_merchant_profile } from "../../../assets/images";
import { CustomInput, ErrorModal } from "../../../components";
import { useForm } from "react-hook-form";
import rules from "../../../rules";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { LoginData } from "../../../types";
import { useNavigate } from "react-router-dom";
import "../../../Pages/Customer/signin/styles.css";
import RegistrationModal from "../../../components/RegistrationModal";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../../Redux/Auth/authSlice";
import VendorMerchantOnboardLeftSide from "../../../components/Vendor/LogInLeftSide";

const LoginVendor = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [userType] = useState<string>("MERCHANT"); // always merchant
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [isChecked, setChecked] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({ mode: "onChange" });

  let access_token: string = "";
  let refresh_token: string = "";

  const decodeToken = (token: any) => {
    return JSON.parse(atob(token.split(".")[1]));
  };

  // LOGIN API
  const handleLogin = useMutation({
    mutationFn: (userData: LoginData) => {
      setIsButtonLoading(true);
      return axiosInstance.post(API_URL.LOGIN, userData);
    },

    onSuccess: (successResponse: any) => {
      access_token = successResponse?.data?.access_token;
      refresh_token = successResponse?.data?.refresh_token;

      const decodedToken: any = decodeToken(access_token);

      const access = decodedToken?.permission; // full-access or partial-access
      const roles = decodedToken?.roles || [];

      setIsButtonLoading(false);

      // MUST BE MERCHANT ONLY
      if (!roles.includes("ROLE_MERCHANT")) {
        setShowErrorModal(true);
        setErrorMessage("Invalid role. Only merchants can login here.");
        return;
      }

      if (access === "full-access") {
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        saveTokenToLocalStorage();
        dispatch(loginSuccess());

        navigate("/vendor/dashboard");
      } else if (access === "partial-access") {
        // Accept login but block dashboard access
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
        setErrorMessage("Access denied");
      }
    },

    onError: (error: any) => {
      setIsButtonLoading(false);
      setErrorMessage(error?.data?.message || "Login failed");
      setShowErrorModal(true);
    },
  });

  const saveTokenToLocalStorage = () => {
    if (isChecked && access_token && refresh_token) {
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
    }
  };

  const combinedValidationRule = {
    validate: (value: any) => {
      const isValidEmail =
        /^(([^<>()[\]\\.+,;:\s@"']+(\.[^<>()[\]\\.,+;:\s@"']+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          value
        );

      const isValidPhone = /^5\d{8}$/.test(value);

      if (!isValidEmail && !isValidPhone) {
        return "Invalid email or phone number";
      }

      return true;
    },
  };

  const onHandleSubmit = () => {
    const userData: LoginData = {
      username: email,
      password: password,
      userType: "MERCHANT", // Always merchant
    };

    handleLogin.mutate(userData);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="outer-container">
      <div className="container-fluid bg-black custom-container">
        <div className="row onboard-garage-main">
          <div className="col-md-6 custom-yellow-bg ">
            <VendorMerchantOnboardLeftSide />
          </div>

          <div className="col-md-6 merchant_login_right text-light background-right-side-onboard ">
            <div className="ps-3 pt-5 pe-2 text-start">
              <div className="d-flex pt-5">
                <img
                  src={kirkos_merchant_profile}
                  className="img-fluid"
                  alt="Icon"
                />
                <p className="merchant_login_text text-light ps-3 pt-3">
                  Vendor Login
                </p>
              </div>

              <div className="merchant_login_headingStyle1 pt-4">
                Username<span className="merchanttopstar">*</span>
              </div>

              <div className="inputContainerStyle pt-3">
                <CustomInput
                  inputName="email"
                  inputType="email"
                  className="merchant_login_input_field"
                  placeholder="email-id or phone number"
                  editable={true}
                  onInputChange={(value: string) => setEmail(value)}
                  control={control}
                  rules={combinedValidationRule}
                />
              </div>

              <div className="merchant_login_headingStyle3 pt-4">
                Password<span className="merchanttopstar">*</span>
              </div>

              <div className="d-flex pt-3 position-relative">
                <CustomInput
                  inputName="password"
                  inputType={showPassword ? "text" : "password"}
                  className="merchant_login_input_field"
                  placeholder="xxxxxx"
                  editable={true}
                  onInputChange={(value: string) => setPassword(value)}
                  control={control}
                  rules={rules.AuthRules.password}
                />
                <span
                  className="fs-5 password_eye"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </span>
              </div>

              <div className="col-12 pt-5">
                <button
                  className="btn all_button_style text-black mb-5 btn"
                  onClick={handleSubmit(onHandleSubmit)}
                  disabled={isButtonLoading || !isValid}
                >
                  SIGN IN
                </button>
              </div>
            </div>
          </div>

          <RegistrationModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            onNav={() => navigate("/vendor")}
            message="Waiting for backoffice approval..."
          />

          <ErrorModal
            isOpen={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            message={errorMessage || "The credentials you entered are invalid"}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginVendor;
